// Authentication module — handles login, registration, Google OAuth, and token refresh
import { t, getDocumentLanguage } from './i18n.ts';
import { showToast } from './utils.ts';
import { trackLogin, trackSignup } from './analytics.ts';

// Storage keys are inlined here (instead of imported from utils.ts) so
// test files that mock the utils module don't have to re-export them —
// auth.ts is the canonical owner of client-side auth state (#1494).
const JWT_KEY  = 'smartpluvia-jwt';   // legacy — drop after one release cycle
const USER_KEY = 'smartpluvia-user';
import { dashboard_path, login_path } from './routes';
import {
  api_v1_auth_login_path,
  api_v1_auth_register_path,
  api_v1_auth_refresh_path,
  api_v1_auth_logout_path,
  api_v1_auth_oauth_google_path,
  api_v1_auth_oauth_facebook_path,
  api_v1_auth_oauth_apple_path,
} from './api-routes';

// Minimal ambient declaration for the Google Identity Services library loaded
// from https://accounts.google.com/gsi/client. We only call the prompt-based
// credential flow, so a focused shape is enough — full typings would require
// the @types/google.accounts dev-dependency.
type GoogleAccountsId = {
  initialize: (config: {
    client_id: string;
    callback: (response: { credential: string }) => void;
    ux_mode?: 'popup' | 'redirect';
  }) => void;
  prompt: () => void;
};
declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleAccountsId } };
  }
}

// #1494: the refresh token lives ONLY in an HttpOnly cookie set by the
// server (REFRESH_TOKEN_COOKIE, path=/api, SameSite=Strict). JS cannot
// read or write it, so XSS on the origin can't exfiltrate the 7-day
// session. The short-lived access JWT (15 min) still mirrors to
// localStorage for the SPA's Authorization header — that's a smaller
// blast radius and a separate follow-up to migrate fully to cookies.

/** Flag to prevent concurrent refresh attempts */
let isRefreshing = false;
/** Queue of callbacks waiting for a token refresh to complete */
let refreshQueue: Array<(token: string | null) => void> = [];

/**
 * Store the user payload from an auth response. As of #1494 the SPA
 * stores NO tokens client-side — both access and refresh JWTs live in
 * HttpOnly cookies (USER_JWT_COOKIE, REFRESH_TOKEN_COOKIE) set by the
 * server. The user object stays in localStorage for synchronous
 * `isAuthenticated()` / `getStoredUser()` checks that need to render
 * UI without a network round-trip; it carries no credentials.
 */
function storeTokens(data: { token?: string; user: unknown }): void {
  localStorage.setItem(USER_KEY, JSON.stringify(data.user));
  // Defensive: drop legacy JWT + refresh-token keys from older builds so
  // pre-#1494 users don't keep a stale exfiltration target lying around.
  localStorage.removeItem(JWT_KEY);
  localStorage.removeItem('smartpluvia-refresh-token');
}

/**
 * Authenticate user with email and password. The server sets both
 * USER_JWT_COOKIE and REFRESH_TOKEN_COOKIE (HttpOnly) on the response;
 * we keep only the user payload client-side (#1494).
 */
export async function login(email: string, password: string) {
  const resp = await fetch(api_v1_auth_login_path(), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || t('auth.invalidCredentials'));
  }
  const data = await resp.json();
  storeTokens(data);
  trackLogin("email");
  return data;
}

/**
 * Register a new user account. Auth cookies set server-side; we keep
 * only the user payload locally (#1494). The page's locale goes with it:
 * the API cannot see which language the form was shown in, and the account
 * locale picks the language of every email and locale redirect after this.
 */
export async function register(name: string, email: string, password: string, passwordConfirmation: string) {
  const resp = await fetch(api_v1_auth_register_path(), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name, email, password, password_confirmation: passwordConfirmation, locale: getDocumentLanguage(),
    }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || t('auth.registrationFailed'));
  }
  const data = await resp.json();
  storeTokens(data);
  trackSignup("email");
  return data;
}

/**
 * Initiate the Google OAuth flow using Google Identity Services (GIS).
 *
 * Reads the OAuth client ID from `<meta name="google-client-id">` (rendered by
 * `_head.html.erb` only when `GOOGLE_CLIENT_ID` is configured) and triggers
 * the GIS popup. The resulting ID token is forwarded to
 * `POST /api/v1/auth/oauth/google` for server-side verification, after which
 * the auth payload is stored locally and the user is redirected.
 */
export function googleOAuth(): void {
  const clientId = document
    .querySelector('meta[name="google-client-id"]')
    ?.getAttribute('content');
  if (!clientId) {
    showToast(t('auth.googleNotConfigured'), 'error');
    return;
  }

  const accountsId = window.google?.accounts?.id;
  if (!accountsId) {
    showToast(t('auth.googleFailed'), 'error');
    return;
  }

  accountsId.initialize({
    client_id: clientId,
    callback: handleGoogleCredential,
    ux_mode: 'popup',
  });
  accountsId.prompt();
}

/**
 * Handle a Google credential response: exchange the ID token for SmartPluvia
 * tokens and redirect to the dashboard. Runs in response to a successful
 * `google.accounts.id.prompt()` flow.
 */
async function handleGoogleCredential(response: { credential: string }): Promise<void> {
  try {
    const result = await fetch(api_v1_auth_oauth_google_path(), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id_token: response.credential, locale: getDocumentLanguage() }),
    });
    if (!result.ok) {
      oauthFailure(result.status, 'auth.googleFailed');
      return;
    }
    const data = await result.json();
    storeTokens(data);
    trackLogin('google');
    window.location.href = postLoginPath();
  } catch {
    showToast(t('auth.googleFailed'), 'error');
  }
}

// A 409 means an account with this email exists but no sign-in provider has
// ever vouched for it (registration does not verify the address), so the
// server will not link the provider: the owner has to use the password.
function oauthFailure(status: number, fallbackKey: string): void {
  if (status === 409) showToast(t('auth.accountExistsUsePassword'), 'warning');
  else showToast(t(fallbackKey), 'error');
}

/**
 * Initiate the Facebook OAuth flow using the Facebook JS SDK (FB.login).
 *
 * Reads the App ID from `<meta name="facebook-app-id">` (rendered by
 * `_head.html.erb` only when `FACEBOOK_APP_ID` is set), initializes the
 * SDK on first call, and triggers `FB.login` with the `email` scope. The
 * resulting access_token is forwarded to `POST /api/v1/auth/oauth/facebook`
 * for server-side `debug_token` verification, after which the auth
 * payload is stored locally and the user is redirected.
 */
export function facebookOAuth(): void {
  const appId = document
    .querySelector('meta[name="facebook-app-id"]')
    ?.getAttribute('content');
  if (!appId) {
    showToast(t('auth.facebookNotConfigured'), 'error');
    return;
  }

  // The Facebook SDK exposes window.FB once the loader script has resolved.
  // Earlier clicks may race — defer to a polling wait of up to 3s before
  // failing closed.
  waitForGlobal<FBSdk>('FB', 3000)
    .then((FB) => {
      FB.init({ appId, version: 'v19.0', cookie: false, xfbml: false });
      FB.login(
        (response) => {
          if (response.status !== 'connected' || !response.authResponse) {
            showToast(t('auth.facebookFailed'), 'error');
            return;
          }
          handleFacebookToken(response.authResponse.accessToken);
        },
        { scope: 'email' },
      );
    })
    .catch(() => showToast(t('auth.facebookFailed'), 'error'));
}

async function handleFacebookToken(accessToken: string): Promise<void> {
  try {
    const result = await fetch(api_v1_auth_oauth_facebook_path(), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_token: accessToken, locale: getDocumentLanguage() }),
    });
    if (!result.ok) {
      oauthFailure(result.status, 'auth.facebookFailed');
      return;
    }
    const data = await result.json();
    storeTokens(data);
    trackLogin('facebook');
    window.location.href = postLoginPath();
  } catch {
    showToast(t('auth.facebookFailed'), 'error');
  }
}

/**
 * Initiate the Sign-in-with-Apple flow using Apple's JS SDK (AppleID.auth).
 *
 * Reads the Services ID from `<meta name="apple-client-id">` (rendered by
 * `_head.html.erb` only when `APPLE_CLIENT_ID` is set), initializes the
 * SDK, and triggers `AppleID.auth.signIn`. The id_token is forwarded to
 * `POST /api/v1/auth/oauth/apple` for JWKS verification. Apple emits the
 * user's name only on the first successful sign-in — when present we
 * forward it.
 */
export function appleOAuth(): void {
  const clientId = document
    .querySelector('meta[name="apple-client-id"]')
    ?.getAttribute('content');
  if (!clientId) {
    showToast(t('auth.appleNotConfigured'), 'error');
    return;
  }

  waitForGlobal<AppleIdAuth>('AppleID', 3000)
    .then(async (AppleID) => {
      AppleID.auth.init({
        clientId,
        scope: 'name email',
        redirectURI: `${window.location.origin}/auth/apple/callback`,
        usePopup: true,
      });
      try {
        const response = await AppleID.auth.signIn();
        const idToken = response?.authorization?.id_token;
        if (!idToken) {
          showToast(t('auth.appleFailed'), 'error');
          return;
        }
        const fullName = response.user?.name;
        const composedName = fullName
          ? [fullName.firstName, fullName.lastName].filter(Boolean).join(' ').trim()
          : undefined;
        handleAppleToken(idToken, composedName);
      } catch {
        showToast(t('auth.appleFailed'), 'error');
      }
    })
    .catch(() => showToast(t('auth.appleFailed'), 'error'));
}

async function handleAppleToken(idToken: string, name?: string): Promise<void> {
  try {
    const result = await fetch(api_v1_auth_oauth_apple_path(), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id_token: idToken, name, locale: getDocumentLanguage() }),
    });
    if (!result.ok) {
      oauthFailure(result.status, 'auth.appleFailed');
      return;
    }
    const data = await result.json();
    storeTokens(data);
    trackLogin('apple');
    window.location.href = postLoginPath();
  } catch {
    showToast(t('auth.appleFailed'), 'error');
  }
}

// Wait until `window[name]` resolves (poll @ 50ms) or `timeoutMs` elapses.
function waitForGlobal<T>(name: string, timeoutMs: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const tick = () => {
      const candidate = (window as unknown as Record<string, unknown>)[name];
      if (candidate) {
        resolve(candidate as T);
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        reject(new Error(`${name} not loaded`));
        return;
      }
      setTimeout(tick, 50);
    };
    tick();
  });
}

interface FBSdk {
  init(options: { appId: string; version: string; cookie?: boolean; xfbml?: boolean }): void;
  login(
    callback: (response: {
      status: string;
      authResponse?: { accessToken: string; userID: string };
    }) => void,
    options?: { scope: string },
  ): void;
}

interface AppleIdAuth {
  auth: {
    init(options: {
      clientId: string;
      scope: string;
      redirectURI: string;
      usePopup?: boolean;
    }): void;
    signIn(): Promise<{
      authorization?: { id_token: string; code: string; state?: string };
      user?: { name?: { firstName?: string; lastName?: string }; email?: string };
    }>;
  };
}

/**
 * Replace the cached user with a fresh server copy (any endpoint that returns
 * the full user entity). No-op when nothing is cached, so a response that
 * lands after a logout in another tab cannot resurrect the session.
 */
export function updateStoredUser(user: unknown): void {
  if (!localStorage.getItem(USER_KEY)) return;
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

/**
 * Retrieve the stored user object from localStorage.
 */
export function getStoredUser() {
  // @ts-expect-error — TS strict
  try { return JSON.parse(localStorage.getItem(USER_KEY)); }
  catch { return null; }
}

/**
 * Sync auth check: does the SPA have a logged-in user state? Reads the
 * USER_KEY payload (not credentials) — the actual JWT lives in the
 * HttpOnly USER_JWT_COOKIE that JS can't see. For the rare network-level
 * truth check, call /api/v1/me directly.
 *
 * Legacy JWT_KEY presence is also accepted so users mid-roll-out (cached
 * SPA bundle from before #1494) keep working until their next /me round-
 * trip refreshes USER_KEY. Drop the fallback after one release cycle.
 */
export function isAuthenticated() {
  return !!localStorage.getItem(USER_KEY) || !!localStorage.getItem(JWT_KEY);
}

/**
 * Attempt to refresh the access token. The refresh token rides as an
 * HttpOnly cookie (#1494); `credentials: 'include'` makes the browser
 * attach it to the request. Single-flight: concurrent callers share
 * the same in-flight request. Returns the new access token on success,
 * or null on failure.
 */
export async function refreshAccessToken(): Promise<string | null> {
  if (isRefreshing) {
    return new Promise<string | null>((resolve) => {
      refreshQueue.push(resolve);
    });
  }

  isRefreshing = true;

  try {
    const resp = await fetch(api_v1_auth_refresh_path(), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });

    if (!resp.ok) {
      clearTokens();
      flushRefreshQueue(null);
      return null;
    }

    const data = await resp.json();
    storeTokens(data);
    flushRefreshQueue(data.token);
    return data.token;
  } catch {
    clearTokens();
    flushRefreshQueue(null);
    return null;
  } finally {
    isRefreshing = false;
  }
}

/**
 * Resolve all queued refresh callbacks with the given token.
 */
function flushRefreshQueue(token: string | null): void {
  refreshQueue.forEach((resolve) => resolve(token));
  refreshQueue = [];
}

/**
 * Remove all stored client-side auth state (USER_KEY + any legacy JWT /
 * refresh-token leftovers). The HttpOnly cookies are cleared server-side
 * by /auth/logout. Exported so callers that hit a 401 on a non-auth
 * endpoint can drop their cached user state without each repeating the
 * exact set of keys to scrub (#1494).
 */
export function clearAuthState(): void {
  localStorage.removeItem(USER_KEY);
  // Defensive: older builds wrote JWT + refresh token to localStorage.
  localStorage.removeItem(JWT_KEY);
  localStorage.removeItem('smartpluvia-refresh-token');
}

function clearTokens(): void {
  clearAuthState();
}

/**
 * Clear stored credentials, revoke server-side tokens, and redirect to login.
 * Auth rides on the HttpOnly cookie (#1494) — no Authorization header needed.
 */
export async function logout() {
  if (isAuthenticated()) {
    try {
      await fetch(api_v1_auth_logout_path(), {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Best-effort server-side revocation; proceed with local cleanup regardless
    }
  }
  clearTokens();
  window.location.href = login_path();
}

/**
 * Redirect to dashboard if user is already authenticated.
 * Call on auth pages to skip login/register for logged-in users.
 */
/**
 * Where to go after signing in: the `next` the page was opened with when it
 * is a path on this site (the admin panel sends a timed-out admin here with
 * one), otherwise the dashboard. Anything that is not a plain same-site path
 * — a protocol-relative "//host", a full URL, a backslash trick — is ignored,
 * so the login page cannot serve as an open redirect.
 */
export function postLoginPath(): string {
  const next = new URLSearchParams(window.location.search || '').get('next');
  if (next && /^\/(?![/\\])/.test(next)) return next;
  return dashboard_path();
}

export function redirectIfAuthenticated() {
  // A `next` target means the sending page wants a fresh sign-in (the admin
  // panel's idle clock restarts only on one): stay on the form rather than
  // bounce a still-cached session away from it.
  if (new URLSearchParams(window.location.search || '').has('next')) return;
  if (isAuthenticated()) {
    window.location.href = dashboard_path();
  }
}

